import React from 'react';
import { fiscalYearShortLabel, fiscalYearLabel } from '../../utils/fiscalYear.mjs';
import { QUARTERS } from '../util/aggregate.mjs';

/**
 * Pieces every Reports tab shares, so the tabs read as one module: the
 * toolbar, the summary tiles, and the loading / error / empty states.
 */

const SELECT = 'min-h-[44px] sm:min-h-0 px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red focus:border-transparent';

/**
 * Fiscal year, region (only when there is more than one to choose), and the
 * tab's Excel export.
 */
export const ReportToolbar = ({
  fiscalYear, fiscalYears, onFiscalYear,
  quarter = 'all', onQuarter,
  regions = [], region = 'all', onRegion,
  onExport, exporting = false, exportDisabled = false, loading = false, note = null,
}) => (
  <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
    <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-3 sm:gap-4">
      {fiscalYears.length > 0 && (
        <select
          aria-label="Fiscal year"
          value={fiscalYear || ''}
          onChange={(e) => onFiscalYear(Number(e.target.value))}
          className={SELECT}
          title={fiscalYear ? fiscalYearLabel(fiscalYear) : undefined}
        >
          {fiscalYears.map(fy => (
            <option key={fy} value={fy}>{fiscalYearShortLabel(fy)} (Sep {fy - 1} - Aug {fy})</option>
          ))}
        </select>
      )}
      {onQuarter && (
        <select
          aria-label="Period"
          value={quarter}
          onChange={(e) => onQuarter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          className={SELECT}
        >
          <option value="all">Full year</option>
          {QUARTERS.map(({ q, label }) => <option key={q} value={q}>Q{q} ({label})</option>)}
        </select>
      )}
      {/* Also shown while one region is selected, even if it is the only one
          known yet, so there is always a way back to All regions. */}
      {(regions.length > 1 || region !== 'all') && onRegion && (
        <select aria-label="Region" value={region} onChange={(e) => onRegion(e.target.value)} className={SELECT}>
          <option value="all">All regions</option>
          {(regions.includes(region) || region === 'all' ? regions : [...regions, region].sort())
            .map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      )}
      {onExport && (
        <button
          onClick={onExport}
          disabled={exporting || exportDisabled}
          className="min-h-[44px] sm:min-h-0 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
        >
          {exporting ? 'Exporting...' : 'Export Excel'}
        </button>
      )}
      {loading && <span className="text-sm text-gray-500" role="status">Loading...</span>}
    </div>
    {note && <p className="mt-3 text-sm text-gray-500">{note}</p>}
  </div>
);

/**
 * The toolbar wired to ReportsPage's scope: fiscal year and quarter for
 * everyone, the region picker only for the roles that see every region.
 * Anything else (export, note, loading) passes straight through.
 */
export const ScopeToolbar = ({ scope, fiscalYears, ...rest }) => (
  <ReportToolbar
    fiscalYear={scope.fy}
    fiscalYears={fiscalYears}
    onFiscalYear={scope.setFy}
    quarter={scope.quarter}
    onQuarter={scope.setQuarter}
    regions={scope.isGlobal ? scope.regions : []}
    region={scope.region}
    onRegion={scope.isGlobal ? scope.setRegion : undefined}
    {...rest}
  />
);

/** A text box for narrowing a table, sized to sit in a Section header. */
export const SearchBox = ({ value, onChange, label = 'Search', placeholder = 'Search...' }) => (
  <input
    type="search"
    aria-label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="min-h-[44px] sm:min-h-0 w-full sm:w-64 px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red focus:border-transparent"
  />
);

/** A row of headline figures. `tone` colours a tile that needs attention. */
export const StatTiles = ({ tiles }) => (
  <div className={`card-grid grid grid-cols-1 sm:grid-cols-2 ${tiles.length >= 5 ? 'lg:grid-cols-5' : tiles.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4 mb-4 sm:mb-6`} style={{ '--card-gap': '1rem' }}>
    {tiles.map(({ label, value, hint, tone }) => (
      <div
        key={label}
        className={`bg-white rounded-xl shadow-lg p-5 ${tone === 'warn' ? 'ring-1 ring-amber-300' : ''}`}
      >
        <div className={`text-2xl font-bold leading-tight tabular-nums truncate ${tone === 'warn' ? 'text-amber-700' : 'text-gray-800'}`}>
          {value}
        </div>
        <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
        {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
      </div>
    ))}
  </div>
);

export const LoadingCard = ({ text = 'Loading...' }) => (
  <div className="bg-white rounded-xl shadow-lg py-16 flex flex-col items-center justify-center">
    <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4"></div>
    <p className="text-gray-600">{text}</p>
  </div>
);

export const ErrorCard = ({ error, onRetry }) => (
  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex flex-wrap items-center gap-3">
    <span>{error}</span>
    {onRetry && (
      <button onClick={onRetry} className="px-3 py-1.5 text-sm border border-red-300 rounded-lg hover:bg-red-100">
        Try again
      </button>
    )}
  </div>
);

/** A titled white card, for one table. */
export const Section = ({ title, subtitle, children }) => (
  <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-4 sm:mb-6">
    <div className="px-4 sm:px-6 pt-4 pb-3 border-b border-gray-100">
      <h2 className="text-base font-semibold text-gray-800">{title}</h2>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
    {children}
  </section>
);

export const TH = 'px-4 py-3 text-left text-sm font-semibold';
export const THR = 'px-4 py-3 text-right text-sm font-semibold';
export const TD = 'px-4 py-3 text-sm text-gray-700';
export const TDR = 'px-4 py-3 text-sm text-gray-700 text-right tabular-nums';

export const EmptyRow = ({ colSpan, text }) => (
  <tr><td colSpan={colSpan} className="p-8 text-center text-gray-500">{text}</td></tr>
);
